import * as React from "react";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PullRequestFile, PullRequestView } from "@/lib/api/pull-requests";

const STATUS_LABELS: Record<string, string> = {
  added: "New",
  removed: "Deleted",
  renamed: "Renamed",
  copied: "Copied",
  modified: "Changed",
  changed: "Changed",
  unchanged: "Unchanged",
};

function lineClass(line: string): string {
  if (line.startsWith("+")) return "bg-green-500/10 text-green-700 dark:text-green-300";
  if (line.startsWith("-")) return "bg-red-500/10 text-red-700 dark:text-red-300";
  if (line.startsWith("@@")) return "text-blue-600 dark:text-blue-300";
  return "";
}

function FileRow({ file, htmlUrl }: { file: PullRequestFile; htmlUrl: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <li className="border-b last:border-b-0">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
        aria-expanded={open}
        onClick={() => { setOpen((v) => !v); }}
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        <span className="font-mono truncate flex-1">
          {file.previousPath && file.previousPath !== file.path ? `${file.previousPath} → ${file.path}` : file.path}
        </span>
        <span className="text-xs text-muted-foreground">{STATUS_LABELS[file.status] ?? file.status}</span>
        <span className="text-xs text-green-600">+{file.additions}</span>
        <span className="text-xs text-red-600">-{file.deletions}</span>
      </button>
      {open && (
        <div className="px-3 pb-3">
          {file.patch === null ? (
            <p className="text-sm text-muted-foreground">
              This file's changes can't be shown here (it may be binary or too large).{" "}
              <a href={`${htmlUrl}/files`} target="_blank" rel="noreferrer" className="underline">
                View it on GitHub
              </a>
            </p>
          ) : (
            <>
              <pre className="overflow-x-auto rounded-md border bg-muted/30 text-xs leading-5">
                {file.patch.split("\n").map((line, i) => (
                  <div key={i} className={`px-2 whitespace-pre ${lineClass(line)}`}>
                    {line || " "}
                  </div>
                ))}
              </pre>
              {file.truncated && (
                <p className="mt-1 text-xs text-muted-foreground">
                  This change is too large to show in full.{" "}
                  <a href={`${htmlUrl}/files`} target="_blank" rel="noreferrer" className="underline">
                    See the rest on GitHub
                  </a>
                </p>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function PullRequestFiles({
  pr,
  page,
  onPageChange,
}: {
  pr: PullRequestView;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const { filesPage } = pr;
  const pages = Math.max(1, Math.ceil(filesPage.total / filesPage.pageSize));
  return (
    <section className="rounded-lg border">
      <header className="flex items-center justify-between border-b px-3 py-2">
        <h2 className="text-sm font-semibold">
          Changed files <span className="text-muted-foreground font-normal">({filesPage.total})</span>
        </h2>
        <a
          href={`${pr.htmlUrl}/files`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          All changes on GitHub <ExternalLink className="h-3 w-3" />
        </a>
      </header>
      {pr.files.length === 0 ? (
        <p className="px-3 py-4 text-sm text-muted-foreground">No file changes.</p>
      ) : (
        <ul>
          {pr.files.map((f) => (
            <FileRow key={f.path} file={f} htmlUrl={pr.htmlUrl} />
          ))}
        </ul>
      )}
      {pages > 1 && (
        <footer className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
          <span>
            Page {page} of {pages}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { onPageChange(page - 1); }}>
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!filesPage.hasMore}
              onClick={() => { onPageChange(page + 1); }}
            >
              Next
            </Button>
          </div>
        </footer>
      )}
    </section>
  );
}
